import React, { useMemo } from 'react';
import { BsThreeDots } from 'react-icons/bs';
import { useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import { Flex, Icon, Image, Tooltip } from '@chakra-ui/react';

import { IconButtonMenu } from '../../../../../../components/library';
import StatusButton from '../../../../../../components/StatusButton';
import { getStatusButtonMenuOptions } from '../../../../../../components/StatusButton/reactQueryUtil';
import { CypressIds } from '../../../../../../constants/cypressIds';
import { InsuredProfilePageDuskIds } from '../../../../../../constants/duskIds';
import { SafeStatuses, Statuses } from '../../../../../../constants/statuses';
import { selectCurrentRecordId } from '../../../../../../redux/modules/insuredProfilePage';
import { selectUser } from '../../../../../../redux/modules/user';
import {
  useArchiveInsuredInsuranceRequirementData,
  useDeleteInsuranceRequirementData,
  useInsuredData,
  useReactivateInsuredInsuranceRequirementData, useUpdateInsuranceRequirementListData
} from '../../../../../../services/insured-service';
import { useRecordDocumentRequirements, useRecordItemRequirements, useRecordPolicyRequirements } from '../../../../../../services/record-service';
import RecordWatcherIconButtonContainer from '../RecordWatcherIconButtonContainer';
import { activeMenuOptions, archivedMenuOptions } from '../util';

export const COLUMNS = [
  {
    Header: 'Project Name',
    accessor: 'job.jobName'
  },
  {
    Header: 'Project Number',
    accessor: 'job.jobNumber'
  },
  {
    Header: 'Record Number',
    accessor: 'contractNumber'
  },
  {
    Header: 'Department Name',
    accessor: 'job.department.departmentName'
  },
  {
    Header: 'Record External Ids',
    id: 'externalIds',
    disableSortBy: true,
    Cell: ({ cell: { value }, row: { original } }) => {
      const recordIntegrations = original?.insuranceRequirementIntegrations ?? [];

      return (
        <Flex alignItems="center" justifyContent="center">
          {recordIntegrations.filter(recordIntegration => !!recordIntegration?.integration?.integrationType?.thumbnailUrl)
            .map(recordIntegration => (
              <Tooltip key={recordIntegration.insuranceRequirementId} label={recordIntegration.externalId}>
                <Image
                  alt="integration image"
                  height="20px"
                  objectFit="contain"
                  src={recordIntegration.integration.integrationType.thumbnailUrl}
                  width="20px"
                />
              </Tooltip>
            ))}
        </Flex>
      );
    }
  },
  {
    Header: 'Watchers',
    id: 'recordWatchers',
    disableSortBy: true,
    Cell: ({ cell: { value }, row: { original } }) => {
      const { companyId } = useParams();
      const insuredId = parseInt(original.insuredId, 10);
      const currentUser = useSelector(selectUser);

      return (
        <Flex alignItems="center" justifyContent="center">
          <RecordWatcherIconButtonContainer
            stopPropagation
            companyId={companyId}
            currentUser={currentUser}
            departmentId={original.job?.department?.departmentId}
            insuredId={insuredId}
            maxHeight="100px"
            placement="left-end"
            recordId={original.id}
          />
        </Flex>
      );
    }
  },
  {
    Header: 'Status',
    accessor: 'recordStatus',
    id: 'recordStatus',
    Cell: ({ cell: { value }, row: { original } }) => {
      const currentRecordId = useSelector(selectCurrentRecordId);
      const { id: requirementId, job, jobId } = original;
      const { data: recordPolicyRequirements, isLoading: policyRequirementsLoading } = useRecordPolicyRequirements(requirementId);
      const { data: recordItemRequirements, isLoading: itemRequirementsLoading } = useRecordItemRequirements(requirementId);
      const { data: recordDocumentRequirements, isLoading: documentRequirementsLoading } = useRecordDocumentRequirements(requirementId);
      const options = value && getStatusButtonMenuOptions(value, 'jobs');
      const isLoading = !value;
      const disabledOptions = { ...options, transitions: options.transitions.filter(o => o.status !== Statuses.Active) };

      const calculatedOptions = useMemo(() => {
        if (!policyRequirementsLoading && !documentRequirementsLoading && !itemRequirementsLoading) {
          const allPolApp = recordPolicyRequirements.every(rp => SafeStatuses.includes(rp.status));
          const allDocApp = recordDocumentRequirements.every(rd => SafeStatuses.includes(rd.status));
          const allItemApp = recordItemRequirements.every(rd => SafeStatuses.includes(rd.status));

          const calcValue = (allPolApp && allDocApp && allItemApp) ? Statuses.Approved : Statuses.NotApproved;

          return job.status === Statuses.Archived ? disabledOptions : getStatusButtonMenuOptions(calcValue, 'jobs');
        }

        if (value) {
          return getStatusButtonMenuOptions(value, 'jobs');
        }

        return null;
      }, [recordPolicyRequirements, recordDocumentRequirements, job, value]);

      const recordStatusFetching = useMemo(
        () => (currentRecordId === requirementId && original?.isLoading),
        [original]
      );

      return (
        <StatusButton
          disabled
          cypressId={CypressIds.recordStatusButton(original.id)}
          duskSelector={InsuredProfilePageDuskIds.getRecordStatusBadge(original.id)}
          id={requirementId}
          isFetching={recordStatusFetching}
          isLoading={isLoading}
          metadata={{ requirementId: original.id, insuredId: original.insuredId, jobId: original.jobId }}
          options={calculatedOptions}
        />
      );
    }
  },
  {
    Header: '',
    id: 'recordOptions',
    disableSortBy: true,
    Cell: ({ cell: { value }, row: { original } }) => {
      const { companyId } = useParams();
      const insuredId = parseInt(original.insuredId, 10);
      const currentUser = useSelector(selectUser);

      const { data: insured, isLoading: insuredLoading } = useInsuredData(insuredId);
      const { mutate: reactivateInsuranceRequirement } = useReactivateInsuredInsuranceRequirementData(insuredId, original.id);
      const { mutate: archiveInsuranceRequirement } = useArchiveInsuredInsuranceRequirementData(insuredId, original.id);
      const { mutate: deleteInsuranceRequirement } = useDeleteInsuranceRequirementData(insuredId, original.id);
      const { mutate: updateInsuranceRequirement } = useUpdateInsuranceRequirementListData(insuredId, original.id);

      const menuOptions = original.recordStatus === Statuses.Archived
        ? archivedMenuOptions(original.job.status === Statuses.Archived || insured?.archivedAt)
        : activeMenuOptions({
          companyName: insured?.insuredCompanyName,
          email: insured?.insuredEmail,
          cc: insured?.insuredCc,
          contractNumber: original.contractNumber,
          requirementEmail: original.contactEmail,
          requirementCc: original.contactCc,
          requirementDescription: original.description,
          requirementId: original.id,
          watcherId: original.watcherId,
          insuredId,
          companyId
        }, currentUser);

      return (
        <IconButtonMenu
          ariaLabel="Record Menu"
          companyId={companyId}
          iconElement={<Icon as={BsThreeDots} boxSize="20px" px={0} />}
          id={original.requirementId}
          insured={insured}
          isLoading={insuredLoading}
          menuOptions={menuOptions}
          metadata={{
            reactivateInsuranceRequirement,
            archiveInsuranceRequirement,
            deleteInsuranceRequirement,
            updateInsuranceRequirement
          }}
          size="sm"
          width="auto"
        />
      );
    }
  }
];
